// Mega Food Pack: 40 food filters in 4 sections, added 2026-10-11 at the owner's request.
// Same format as SIMPLE_FILTERS (see simpleFilters.ts): "add" moves a value, "set" goes to a value, and
// Strength scales every change. These are starting values for the owner to tune on real menu photos.
// Rules (enforced by the existing tests once these are merged into the food group):
//   - never touch hsl.green.h or hsl.yellow.h (food greens and yellows keep their hue)
//   - stay inside the clamp ranges in simpleFilters.ts
import type { Pt } from '../model/types';
import type { SimpleFilter } from './simpleFilters';

export type FoodSection = 'fix' | 'dish' | 'style' | 'menu';

export const FOOD_SECTIONS: { id: FoodSection; name: string; nameEs: string }[] = [
  { id: 'fix', name: 'Fix the light', nameEs: 'Arreglar la luz' },
  { id: 'dish', name: 'By dish', nameEs: 'Por platillo' },
  { id: 'style', name: 'Styles', nameEs: 'Estilos' },
  { id: 'menu', name: 'Menu & delivery', nameEs: 'Menú y delivery' },
];

type Op = SimpleFilter['ops'][number];
type PackFilter = SimpleFilter & { section: FoodSection };

const lift = (black: number, white = 1): Pt[] => [
  { x: 0, y: black },
  { x: 1, y: white },
];
const p = (
  id: string,
  section: FoodSection,
  name: string,
  nameEs: string,
  ops: Op[],
  curve?: Pt[],
): PackFilter => ({ id: `fp-${id}`, group: 'food', section, name, nameEs, ops, ...(curve ? { curve } : {}) });

export const FOOD_PACK: PackFilter[] = [
  // ── Fix the light: restaurant lighting problems ─────────────────────────────
  p('kitchen-light', 'fix', 'Kitchen Light Fix', 'Luz de cocina', [
    ['wb.temp', 'add', -20],
    ['wb.tint', 'add', 2],
    ['light.highlights', 'add', -10],
    ['color.vibrance', 'add', 5],
  ]),
  p('fluorescent', 'fix', 'Fluorescent Fix', 'Luz fluorescente', [
    ['wb.tint', 'add', 14],
    ['wb.temp', 'add', 4],
    ['light.contrast', 'add', 5],
  ]),
  p('flash', 'fix', 'Flash Fix', 'Quitar flash', [
    ['light.highlights', 'add', -45],
    ['light.whites', 'add', -15],
    ['light.shadows', 'add', 20],
    ['light.contrast', 'add', -10],
    ['wb.temp', 'add', 6],
  ]),
  p('dim-room', 'fix', 'Dim Room', 'Restaurante oscuro', [
    ['light.exposure', 'add', 0.5],
    ['light.shadows', 'add', 35],
    ['light.contrast', 'add', 5],
    ['detail.nrLuma', 'add', 25],
    ['detail.nrColor', 'add', 30],
  ]),
  p('window-light', 'fix', 'Window Light', 'Luz de ventana', [
    ['light.highlights', 'add', -40],
    ['light.shadows', 'add', 25],
    ['light.whites', 'add', -5],
    ['wb.temp', 'add', -2],
  ]),
  p('patio-shade', 'fix', 'Patio Shade', 'Sombra de patio', [
    ['wb.temp', 'add', 8],
    ['light.exposure', 'add', 0.2],
    ['color.vibrance', 'add', 10],
  ]),
  p('harsh-sun', 'fix', 'Harsh Sun', 'Sol fuerte', [
    ['light.highlights', 'add', -55],
    ['light.shadows', 'add', 30],
    ['light.contrast', 'add', -10],
    ['light.whites', 'add', -10],
  ]),

  // ── By dish ──────────────────────────────────────────────────────────────────
  p('burger', 'dish', 'Burger & Fries', 'Hamburguesa', [
    ['light.contrast', 'add', 15],
    ['light.shadows', 'add', 10],
    ['color.vibrance', 'add', 15],
    ['wb.temp', 'add', 6],
    ['hsl.orange.s', 'add', 10],
    ['hsl.red.s', 'add', 5],
    ['detail.sharpAmount', 'add', 45],
  ]),
  p('pizza', 'dish', 'Pizza', 'Pizza', [
    ['wb.temp', 'add', 8],
    ['light.highlights', 'add', -20],
    ['light.contrast', 'add', 10],
    ['color.vibrance', 'add', 15],
    ['hsl.red.s', 'add', 8],
    ['hsl.orange.s', 'add', 6],
    ['detail.sharpAmount', 'add', 35],
  ]),
  p('tacos', 'dish', 'Tacos', 'Tacos', [
    ['light.contrast', 'add', 10],
    ['color.vibrance', 'add', 20],
    ['wb.temp', 'add', 4],
    ['hsl.green.s', 'add', 12],
    ['hsl.red.s', 'add', 6],
    ['detail.sharpAmount', 'add', 35],
  ]),
  p('chapin', 'dish', 'Guatemalan Plate', 'Plato chapín', [
    ['wb.temp', 'add', 6],
    ['light.contrast', 'add', 8],
    ['light.shadows', 'add', 10],
    ['color.vibrance', 'add', 15],
    ['hsl.red.s', 'add', 6],
    ['hsl.orange.s', 'add', 6],
  ]),
  p('corn-tortilla', 'dish', 'Corn & Tortilla', 'Maíz y tortilla', [
    ['wb.temp', 'add', 5],
    ['light.highlights', 'add', -15],
    ['hsl.yellow.s', 'add', 6],
    ['hsl.yellow.l', 'add', 4],
    ['detail.sharpAmount', 'add', 30],
  ]),
  p('grill', 'dish', 'Grill & BBQ', 'Parrilla', [
    ['light.contrast', 'add', 18],
    ['light.blacks', 'add', -8],
    ['wb.temp', 'add', 10],
    ['hsl.red.s', 'add', 6],
    ['hsl.orange.l', 'add', -4],
    ['effects.vignette.amount', 'add', -15],
    ['detail.sharpAmount', 'add', 45],
  ]),
  p('fried-chicken', 'dish', 'Fried Chicken', 'Pollo frito', [
    ['wb.temp', 'add', 8],
    ['light.highlights', 'add', -20],
    ['light.contrast', 'add', 12],
    ['hsl.orange.s', 'add', 10],
    ['hsl.orange.l', 'add', 4],
    ['detail.sharpAmount', 'add', 45],
  ]),
  p('seafood', 'dish', 'Seafood', 'Mariscos', [
    ['wb.temp', 'add', -8],
    ['color.vibrance', 'add', 12],
    ['hsl.aqua.s', 'add', 8],
    ['hsl.orange.s', 'add', 6],
    ['cleanWhites.amount', 'add', 30],
    ['detail.sharpAmount', 'add', 35],
  ]),
  p('sushi', 'dish', 'Sushi', 'Sushi', [
    ['wb.temp', 'add', -5],
    ['light.contrast', 'add', 8],
    ['hsl.red.s', 'add', 8],
    ['hsl.orange.l', 'add', 4],
    ['cleanWhites.amount', 'add', 25],
    ['detail.sharpAmount', 'add', 40],
  ]),
  p(
    'ramen',
    'dish',
    'Ramen & Soup',
    'Ramen y sopa',
    [
      ['wb.temp', 'add', 8],
      ['light.highlights', 'add', -25],
      ['light.shadows', 'add', 10],
      ['light.contrast', 'add', 6],
      ['color.vibrance', 'add', 10],
    ],
    lift(0.02),
  ),
  p('salad-bowl', 'dish', 'Salad Bowl', 'Bowl de ensalada', [
    ['wb.temp', 'add', -4],
    ['color.vibrance', 'add', 18],
    ['hsl.green.s', 'add', 12],
    ['hsl.green.l', 'add', 6],
    ['hsl.red.s', 'add', 5],
    ['cleanWhites.amount', 'add', 20],
    ['detail.sharpAmount', 'add', 35],
  ]),
  p('breakfast', 'dish', 'Breakfast', 'Desayuno', [
    ['light.exposure', 'add', 0.25],
    ['light.highlights', 'add', -15],
    ['light.shadows', 'add', 15],
    ['wb.temp', 'add', 6],
    ['hsl.yellow.s', 'add', 8],
    ['hsl.orange.s', 'add', 5],
  ]),
  p('bakery', 'dish', 'Bakery & Bread', 'Panadería', [
    ['wb.temp', 'add', 12],
    ['light.contrast', 'add', 10],
    ['light.highlights', 'add', -10],
    ['hsl.orange.s', 'add', 6],
    ['hsl.orange.l', 'add', -3],
    ['detail.sharpAmount', 'add', 50],
  ]),
  p('latte', 'dish', 'Latte Art', 'Café con arte', [
    ['wb.temp', 'add', 6],
    ['light.contrast', 'add', 12],
    ['light.highlights', 'add', -25],
    ['color.saturation', 'add', -5],
    ['cleanWhites.amount', 'add', 20],
    ['detail.sharpAmount', 'add', 40],
  ]),
  p('smoothie', 'dish', 'Smoothie & Juice', 'Licuado y jugo', [
    ['light.exposure', 'add', 0.2],
    ['light.highlights', 'add', -15],
    ['color.vibrance', 'add', 25],
    ['color.saturation', 'add', 5],
    ['hsl.magenta.s', 'add', 10],
    ['hsl.orange.s', 'add', 8],
  ]),
  p('cocktail', 'dish', 'Cocktail Bar', 'Coctelería', [
    ['light.contrast', 'add', 15],
    ['light.blacks', 'add', -10],
    ['color.vibrance', 'add', 15],
    ['hsl.aqua.s', 'add', 10],
    ['hsl.blue.s', 'add', 8],
    ['effects.vignette.amount', 'add', -20],
    ['effects.splitTone.shadowHue', 'set', 220],
    ['effects.splitTone.shadowSat', 'add', 10],
  ]),
  p('cold-beer', 'dish', 'Cold Beer', 'Cerveza fría', [
    ['wb.temp', 'add', -4],
    ['light.contrast', 'add', 12],
    ['light.highlights', 'add', -20],
    ['hsl.orange.s', 'add', 10],
    ['hsl.orange.l', 'add', 5],
    ['detail.sharpAmount', 'add', 40],
  ]),
  p('chocolate', 'dish', 'Chocolate', 'Chocolate', [
    ['light.contrast', 'add', 15],
    ['light.highlights', 'add', -15],
    ['light.blacks', 'add', -6],
    ['wb.temp', 'add', 5],
    ['hsl.orange.l', 'add', -6],
    ['hsl.red.s', 'add', 5],
    ['detail.sharpAmount', 'add', 35],
  ]),
  p(
    'ice-cream',
    'dish',
    'Ice Cream',
    'Helado',
    [
      ['light.exposure', 'add', 0.3],
      ['light.shadows', 'add', 20],
      ['color.vibrance', 'add', 18],
      ['hsl.magenta.s', 'add', 8],
      ['hsl.aqua.s', 'add', 6],
      ['cleanWhites.amount', 'add', 25],
    ],
    lift(0.03),
  ),
  p('fruit', 'dish', 'Fresh Fruit', 'Fruta fresca', [
    ['light.contrast', 'add', 10],
    ['color.vibrance', 'add', 25],
    ['hsl.red.s', 'add', 8],
    ['hsl.orange.s', 'add', 6],
    ['hsl.magenta.s', 'add', 6],
    ['detail.sharpAmount', 'add', 35],
  ]),

  // ── Styles ───────────────────────────────────────────────────────────────────
  p('white-marble', 'style', 'White Marble', 'Mármol blanco', [
    ['light.exposure', 'add', 0.3],
    ['light.contrast', 'add', 5],
    ['light.shadows', 'add', 15],
    ['wb.temp', 'add', -4],
    ['cleanWhites.amount', 'add', 50],
  ]),
  p('rustic-wood', 'style', 'Rustic Wood', 'Madera rústica', [
    ['wb.temp', 'add', 10],
    ['light.contrast', 'add', 12],
    ['color.saturation', 'add', -5],
    ['hsl.orange.s', 'add', 4],
    ['hsl.orange.l', 'add', -5],
    ['effects.vignette.amount', 'add', -15],
  ]),
  p('overhead', 'style', 'Overhead Flat Lay', 'Vista cenital', [
    ['light.exposure', 'add', 0.2],
    ['light.contrast', 'add', 8],
    ['light.shadows', 'add', 15],
    ['color.vibrance', 'add', 15],
    ['detail.sharpAmount', 'add', 40],
  ]),
  p('market', 'style', 'Market Colors', 'Colores de mercado', [
    ['light.contrast', 'add', 12],
    ['color.vibrance', 'add', 30],
    ['color.saturation', 'add', 5],
    ['detail.sharpAmount', 'add', 30],
  ]),
  p('neon-night', 'style', 'Neon Night', 'Noche neón', [
    ['light.contrast', 'add', 15],
    ['light.blacks', 'add', -10],
    ['color.vibrance', 'add', 20],
    ['hsl.magenta.s', 'add', 15],
    ['hsl.blue.s', 'add', 12],
    ['effects.splitTone.shadowHue', 'set', 240],
    ['effects.splitTone.shadowSat', 'add', 15],
    ['effects.splitTone.highlightHue', 'set', 320],
    ['effects.splitTone.highlightSat', 'add', 10],
    ['effects.vignette.amount', 'add', -15],
  ]),
  p('candlelight', 'style', 'Candlelight', 'Luz de vela', [
    ['wb.temp', 'add', 18],
    ['light.exposure', 'add', 0.1],
    ['light.highlights', 'add', -15],
    ['effects.vignette.amount', 'add', -25],
    ['effects.splitTone.highlightHue', 'set', 40],
    ['effects.splitTone.highlightSat', 'add', 15],
    ['effects.grain.amount', 'add', 8],
  ]),
  p(
    'film-food',
    'style',
    'Film Food',
    'Comida en película',
    [
      ['color.saturation', 'add', -8],
      ['light.contrast', 'add', 6],
      ['wb.temp', 'add', 5],
      ['effects.splitTone.shadowHue', 'set', 200],
      ['effects.splitTone.shadowSat', 'add', 8],
      ['effects.splitTone.highlightHue', 'set', 45],
      ['effects.splitTone.highlightSat', 'add', 10],
      ['effects.grain.amount', 'add', 15],
    ],
    lift(0.04),
  ),
  p('magazine', 'style', 'Magazine', 'Revista', [
    ['light.contrast', 'add', 10],
    ['light.highlights', 'add', -25],
    ['light.shadows', 'add', 10],
    ['color.saturation', 'add', -5],
    ['color.vibrance', 'add', 12],
    ['cleanWhites.amount', 'add', 20],
    ['detail.sharpAmount', 'add', 45],
  ]),
  p(
    'pastel-brunch',
    'style',
    'Pastel Brunch',
    'Brunch pastel',
    [
      ['light.exposure', 'add', 0.35],
      ['light.contrast', 'add', -10],
      ['light.shadows', 'add', 20],
      ['color.saturation', 'add', -8],
      ['color.vibrance', 'add', 10],
      ['hsl.magenta.l', 'add', 6],
    ],
    lift(0.05),
  ),

  // ── Menu & delivery ──────────────────────────────────────────────────────────
  p('delivery-pop', 'menu', 'Delivery App Pop', 'App de delivery', [
    ['light.exposure', 'add', 0.2],
    ['light.contrast', 'add', 12],
    ['light.highlights', 'add', -20],
    ['color.vibrance', 'add', 25],
    ['cleanWhites.amount', 'add', 30],
    ['detail.sharpAmount', 'add', 55],
  ]),
  p('menu-board', 'menu', 'Menu Board', 'Pizarra de menú', [
    ['light.exposure', 'add', 0.3],
    ['light.contrast', 'add', 15],
    ['light.whites', 'add', 10],
    ['color.vibrance', 'add', 20],
    ['detail.sharpAmount', 'add', 50],
  ]),
  p('printed-menu', 'menu', 'Printed Menu', 'Menú impreso', [
    ['light.contrast', 'add', 8],
    ['light.highlights', 'add', -15],
    ['color.vibrance', 'add', 10],
    ['color.saturation', 'add', -3],
    ['detail.sharpAmount', 'add', 60],
  ]),
  p('instagram-feed', 'menu', 'Instagram Feed', 'Feed de Instagram', [
    ['light.exposure', 'add', 0.15],
    ['light.contrast', 'add', 10],
    ['light.highlights', 'add', -20],
    ['light.shadows', 'add', 12],
    ['wb.temp', 'add', 3],
    ['color.vibrance', 'add', 18],
    ['detail.sharpAmount', 'add', 35],
  ]),
];
